import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { getStudentProfiles } from "../../../services/StudentProfileService";
import { getTeacherProfiles } from "../../../services/TeacherProfileService";
import { getExams } from "../../../services/ExamService";
import { getExamSessions } from "../../../services/ExamSessionService";
import { getAttempts } from "../../../services/StudentAttemptService";
import { getOverviewStatistics } from "../../../services/StatisticsService";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../../utils/i18nLabels";
import { usePermissions } from "../../../utils/permissions";

const statCard = (title, value, icon, color) => ({ title, value, icon, color });

const Dashboard1 = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const userName = useSelector((state) => state.auth.auth?.user?.email) || "";
  // SUPER_ADMIN inclus (userCan applique la même règle que le backend)
  const isStaff = can("STUDENT_PROFILE_LIST");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [counts, setCounts] = useState({ students: null, teachers: null, examsPublished: null, sessionsOngoing: null });
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [recentAttempts, setRecentAttempts] = useState([]);
  const [passRate, setPassRate] = useState(null);
  const [availableSessions, setAvailableSessions] = useState([]);

  useEffect(() => {
    const loadStaffDashboard = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const [ongoingRes, scheduledRes, attemptsRes] = await Promise.all([
          getExamSessions({ page: 1, limit: 5, status: "ongoing" }),
          getExamSessions({ page: 1, limit: 5, status: "scheduled" }),
          getAttempts({ page: 1, limit: 6, sort_by: "started_at", sort_dir: "desc" }),
        ]);
        setUpcomingSessions([...(ongoingRes.data?.data || []), ...(scheduledRes.data?.data || [])].slice(0, 5));
        setRecentAttempts(attemptsRes.data?.data || []);

        try {
          const overviewRes = await getOverviewStatistics();
          const overview = overviewRes.data?.data;
          setCounts({
            students: overview.students,
            teachers: overview.teachers,
            examsPublished: overview.exams.published,
            sessionsOngoing: overview.sessions.ongoing,
          });
          setPassRate(overview.results.passRate);
        } catch (statsErr) {
          // Compte staff sans la permission STATISTIC_LIST (ex. Scolarité) :
          // on retombe sur une approximation via les listes accessibles.
          const [studentsRes, teachersRes, examsRes] = await Promise.all([
            getStudentProfiles({ page: 1, limit: 1 }),
            getTeacherProfiles({ page: 1, limit: 1 }),
            getExams({ page: 1, limit: 1, status: "published" }),
          ]);
          setCounts({
            students: studentsRes.data?.pagination?.total ?? 0,
            teachers: teachersRes.data?.pagination?.total ?? 0,
            examsPublished: examsRes.data?.pagination?.total ?? 0,
            sessionsOngoing: ongoingRes.data?.pagination?.total ?? 0,
          });
          setPassRate(null);
        }
      } catch (err) {
        setLoadError(err?.response?.data?.message || t("common.ui.impossibleChargerStatistiques"));
      } finally {
        setLoading(false);
      }
    };

    const loadStudentDashboard = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const [ongoingRes, scheduledRes] = await Promise.all([
          getExamSessions({ page: 1, limit: 5, status: "ongoing" }),
          getExamSessions({ page: 1, limit: 5, status: "scheduled" }),
        ]);
        setAvailableSessions([...(ongoingRes.data?.data || []), ...(scheduledRes.data?.data || [])].slice(0, 5));
      } catch (err) {
        setLoadError(err?.response?.data?.message || t("pages.dashboard.examsLoadError"));
      } finally {
        setLoading(false);
      }
    };

    if (isStaff) loadStaffDashboard();
    else loadStudentDashboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff]);

  const statusLabels = { in_progress: t("common.examSessionStatus.ongoing"), submitted: t("common.attemptStatus.submitted"), timed_out: t("common.attemptStatus.timed_out"), cancelled: t("common.examSessionStatus.cancelled") };
  const statusBadge = { in_progress: "primary", submitted: "success", timed_out: "warning", cancelled: "danger" };

  const staffCards = useMemo(
    () => [
      statCard(t("menu.students.title"), counts.students, "ti ti-school", "primary"),
      statCard(t("menu.teachers.title"), counts.teachers, "ti ti-user-check", "info"),
      statCard(t("common.ui.examensPublies"), counts.examsPublished, "ti ti-clipboard-text", "success"),
      statCard(t("common.ui.sessionsCours"), counts.sessionsOngoing, "ti ti-broadcast", "warning"),
    ],
    [counts, t],
  );

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("common.crud.loading")}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="bg-welcome-banner mb-3">
          <div className="card-body d-flex align-items-center justify-content-between">
            <h4 className="mb-0 text-white">{t("pages.dashboard.welcome", { name: userName })}</h4>
            {can("STATISTIC_LIST") && (
              <Link to="/statistics" className="btn btn-outline-light btn-sm text-white">
                <i className="ti ti-chart-bar me-1"></i>{t("pages.dashboard.detailedStatistics")}
              </Link>
            )}
          </div>
        </div>

        {loadError && <div className="alert alert-warning">{loadError}</div>}

        {isStaff ? (
          <>
            <div className="row">
              {staffCards.map((card) => (
                <div className="col-xl-4 col-sm-6 mb-3" key={card.title}>
                  <div className="card h-100">
                    <div className="card-body d-flex align-items-center justify-content-between">
                      <div>
                        <p className="mb-1 text-muted">{card.title}</p>
                        <h3 className="mb-0">{card.value ?? "-"}</h3>
                      </div>
                      <span className={`avatar avatar-lg bg-${card.color}-transparent rounded-circle d-flex align-items-center justify-content-center`}>
                        <i className={`${card.icon} text-${card.color} fs-24`}></i>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              <div className="col-xl-4 col-sm-6 mb-3">
                <div className="card h-100">
                  <div className="card-body d-flex align-items-center justify-content-between">
                    <div>
                      <p className="mb-1 text-muted">{t("common.ui.tauxReussite")}</p>
                      <h3 className="mb-0">{passRate !== null ? `${passRate}%` : "-"}</h3>
                      <small className="text-muted">{passRate !== null ? t("pages.dashboard.passRateAllResults") : t("pages.dashboard.passRateUnavailable")}</small>
                    </div>
                    <span className="avatar avatar-lg bg-success-transparent rounded-circle d-flex align-items-center justify-content-center">
                      <i className="ti ti-chart-pie text-success fs-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-lg-6 mb-3">
                <div className="card h-100">
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">{t("pages.dashboard.upcomingSessions")}</h5>
                    <Link to="/exam-sessions" className="btn btn-sm btn-outline-primary">
                      {t("pages.dashboard.viewAll")}
                    </Link>
                  </div>
                  <div className="card-body p-0">
                    {upcomingSessions.length > 0 ? (
                      <div className="list-group list-group-flush">
                        {upcomingSessions.map((session) => (
                          <div key={session.id} className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                              <p className="mb-0 fw-medium">{t("pages.dashboard.session")} {new Date(session.start_at).toLocaleString(getLocale())}</p>
                              <small className="text-muted">{t("pages.dashboard.jusqu")} {new Date(session.end_at).toLocaleString(getLocale())}</small>
                            </div>
                            <span className={`badge badge-soft-${session.status === "ongoing" ? "primary" : "info"}`}>
                              {session.status === "ongoing" ? t("common.examSessionStatus.ongoing") : t("common.examSessionStatus.scheduled")}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted text-center py-4 mb-0">{t("pages.dashboard.noUpcomingSessions")}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6 mb-3">
                <div className="card h-100">
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">{t("pages.dashboard.recentAttempts")}</h5>
                    <Link to="/attempts" className="btn btn-sm btn-outline-primary">
                      {t("pages.dashboard.viewAll")}
                    </Link>
                  </div>
                  <div className="card-body p-0">
                    {recentAttempts.length > 0 ? (
                      <div className="list-group list-group-flush">
                        {recentAttempts.map((attempt) => (
                          <div key={attempt.id} className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                              <p className="mb-0 fw-medium">
                                {attempt.started_at ? new Date(attempt.started_at).toLocaleString(getLocale()) : "-"}
                              </p>
                            </div>
                            <span className={`badge badge-soft-${statusBadge[attempt.status] || "secondary"}`}>
                              {statusLabels[attempt.status] || attempt.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted text-center py-4 mb-0">{t("pages.dashboard.noRecentAttempts")}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="row">
            <div className="col-lg-8 mb-3">
              <div className="card h-100">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">{t("common.ui.examensDisponibles")}</h5>
                  <Link to="/my-exams" className="btn btn-sm btn-primary">
                    {t("pages.dashboard.viewMyExams")}
                  </Link>
                </div>
                <div className="card-body p-0">
                  {availableSessions.length > 0 ? (
                    <div className="list-group list-group-flush">
                      {availableSessions.map((session) => (
                        <div key={session.id} className="list-group-item d-flex justify-content-between align-items-center">
                          <div>
                            <p className="mb-0 fw-medium">{t("pages.dashboard.session")} {new Date(session.start_at).toLocaleString(getLocale())}</p>
                            <small className="text-muted">{t("pages.dashboard.jusqu")} {new Date(session.end_at).toLocaleString(getLocale())}</small>
                          </div>
                          <span className={`badge badge-soft-${session.status === "ongoing" ? "primary" : "info"}`}>
                            {session.status === "ongoing" ? t("common.examSessionStatus.ongoing") : t("common.examSessionStatus.scheduled")}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted text-center py-4 mb-0">{t("pages.dashboard.aucunExamenDisponiblePourMoment")}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-4 mb-3">
              <div className="card h-100">
                <div className="card-body text-center d-flex flex-column justify-content-center">
                  <i className="ti ti-bell fs-1 text-primary mb-2"></i>
                  <p className="mb-2">{t("pages.dashboard.checkNotifications")}</p>
                  <Link to="/notifications" className="btn btn-outline-primary">
                    {t("pages.dashboard.viewMyNotifications")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard1;
